// frontend/src/pages/TeacherSubjects.jsx
import Snackbar from '@mui/material/Snackbar'; // Snackbarをインポート
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Grid, // Grid をレイアウト調整用にインポート
  useTheme // useTheme をインポート
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // コピーアイコン

export default function TeacherSubjects() {
  const theme = useTheme(); // テーマを取得
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false); // コピー成功時など短いメッセージ用

  // 新規科目作成用の状態
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newSubjectDesc, setNewSubjectDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // 編集用の状態
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchSubjects = async () => {
    setLoading(true);
    setError('');
    // setMessage(''); // 長いメッセージは残し、snackbar用の短いメッセージは別途管理
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/subjects/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjects(response.data || []);
    } catch (err) {
      console.error("Error fetching subjects:", err);
      setError(err.response?.data?.detail || '担当科目の取得に失敗しました。');
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (subject) => {
    setEditingId(subject.id);
    setEditName(subject.name || '');
    setEditDesc(subject.description || '');
    setError('');
    setMessage('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDesc('');
  };

  const saveEdit = async (subjectId) => {
    const name = editName.trim();
    if (!name) {
      setError('科目名を入力してください。');
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/subjects/${subjectId}`, {
        name,
        description: editDesc || null,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage('科目情報を更新しました。');
      setEditingId(null);
      setEditName('');
      setEditDesc('');
      await fetchSubjects();
    } catch (err) {
      console.error('Error updating subject:', err);
      setError(err.response?.data?.detail || '科目の更新に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  const handleCreateSubject = async () => {
    setError('');
    setMessage('');
    const name = newSubjectName.trim();
    if (!name) {
      setError('科目名を入力してください。');
      return;
    }
    try {
      setCreating(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/subjects/`, {
        name,
        description: newSubjectDesc || null,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(`科目「${name}」を作成しました。`);
      setNewSubjectName('');
      setNewSubjectDesc('');
      await fetchSubjects();
    } catch (err) {
      console.error('Error creating subject:', err);
      setError(err.response?.data?.detail || '科目の作成に失敗しました。別名でお試しください。');
    } finally {
      setCreating(false);
    }
  };

  const handleGenerateClassCode = async (subjectId) => {
    setMessage(''); // 古いメッセージをクリア
    setLoading(true); // 操作中はローディング表示にしても良い
    try {
      const token = localStorage.getItem('token');
      // ★APIエンドポイント名は実際のものに合わせてください (PUTかPOSTかなども)
      await axios.put(`${API_URL}/subjects/${subjectId}/class-code`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(`科目ID ${subjectId} のクラスコードを生成/更新しました。`);
      fetchSubjects(); // リストを再取得して更新
    } catch (err) {
      console.error("Error generating class code:", err);
      setError(err.response?.data?.detail || 'クラスコードの生成に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCodeActive = async (subjectId, currentIsActive) => {
    setMessage('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // ★APIエンドポイント名は実際のものに合わせてください
      await axios.patch(`${API_URL}/subjects/${subjectId}/class-code/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(`科目ID ${subjectId} のクラスコードを「${currentIsActive ? '無効' : '有効'}」にしました。`);
      fetchSubjects(); // リストを再取得
    } catch (err) {
      console.error("Error toggling class code active status:", err);
      setError(err.response?.data?.detail || 'クラスコード状態の更新に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // setMessage('クラスコードをクリップボードにコピーしました。'); // これだと長いメッセージが上書きされる
      setSnackbarOpen(true); // Snackbarで短いメッセージを表示
    }).catch(err => {
      console.error('クリップボードへのコピーに失敗しました。', err);
      setError('クリップボードへのコピーに失敗しました。');
    });
  };


  if (loading && subjects.length === 0) { // 初回ローディング時
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg"> {/* maxWidthを少し広げてみる */}
      {/* 新規科目の作成 */}
      <Paper sx={{ p: 2.5, mt: 3, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>新規科目の作成</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
          <input
            type="text"
            placeholder="科目名 (必須)"
            value={newSubjectName}
            onChange={(e) => setNewSubjectName(e.target.value)}
            style={{
              flex: 2,
              padding: '10px 12px',
              borderRadius: 4,
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          />
          <input
            type="text"
            placeholder="説明 (任意)"
            value={newSubjectDesc}
            onChange={(e) => setNewSubjectDesc(e.target.value)}
            style={{
              flex: 3,
              padding: '10px 12px',
              borderRadius: 4,
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
          />
          <Button
            variant="contained"
            onClick={handleCreateSubject}
            disabled={creating}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {creating ? '作成中…' : '科目を作成'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          作成すると自動的に担当科目として割り当てられ、学生はクラスコードで履修できます。
        </Typography>
      </Paper>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 3 }}>
        <Typography variant="h4" component="h1">
          科目とクラスコードの管理
        </Typography>
        <Tooltip title="担当科目情報を再取得">
          <IconButton onClick={() => fetchSubjects()} color="primary" disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      
      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="クラスコードをクリップボードにコピーしました。"
      />

      {subjects.length === 0 && !loading && !error && (
        <Paper sx={{ textAlign: 'center', py: 5, mt: 3 }}>
          <Typography variant="h6" color="text.secondary">
            担当している科目がありません。
          </Typography>
        </Paper>
      )}

      <Grid container spacing={3}> {/* Gridレイアウトに変更 */}
        {subjects.map((subject) => (
          <Grid item xs={12} md={6} key={subject.id}> {/* 1列または2列で表示 */}
            <Paper elevation={3} sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}> {/* Paperに高さを合わせる設定 */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                {editingId === subject.id ? (
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="科目名"
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      borderRadius: 4,
                      border: `1px solid ${theme.palette.divider}`,
                      fontSize: 18,
                      fontWeight: 600,
                    }}
                  />
                ) : (
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                    {subject.name}
                  </Typography>
                )}
                <Chip
                  icon={subject.code_active ? <ToggleOnIcon /> : <ToggleOffIcon />}
                  label={subject.code_active ? '受付中' : '停止中'}
                  color={subject.code_active ? 'success' : 'default'}
                  size="small"
                  variant="outlined" // アウトラインに変更
                />
              </Box>
              {editingId === subject.id ? (
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  placeholder="説明 (任意)"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.divider}`,
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    marginBottom: 12,
                  }}
                />
              ) : (
                <>
                  {subject.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                      {subject.description}
                    </Typography>
                  )}
                  {!subject.description && <Box sx={{flexGrow:1}} /> }
                </>
              )}


              <Typography variant="subtitle2" sx={{ mb: 0.5, color: theme.palette.text.secondary }}>
                クラスコード:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, minHeight: '32px' }}>
                {subject.class_code ? (
                  <>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        fontWeight: 'bold', 
                        letterSpacing: 1,
                        mr: 1,
                        backgroundColor: theme.palette.action.hover, // 少し背景色をつける
                        padding: theme.spacing(0.5, 1),
                        borderRadius: theme.shape.borderRadius,
                      }}
                    >
                      {subject.class_code}
                    </Typography>
                    <Tooltip title="クラスコードをコピー">
                      <IconButton size="small" onClick={() => handleCopyToClipboard(subject.class_code)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    未生成
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />
              
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {editingId === subject.id ? (
                    <>
                      <Button variant="contained" size="small" onClick={() => saveEdit(subject.id)} disabled={saving}>
                        {saving ? '保存中…' : '保存'}
                      </Button>
                      <Button variant="text" size="small" onClick={cancelEdit} disabled={saving}>取消</Button>
                    </>
                  ) : (
                    <Button variant="outlined" size="small" onClick={() => startEdit(subject)}>編集</Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<VpnKeyIcon />}
                    onClick={() => handleGenerateClassCode(subject.id)}
                    disabled={loading}
                    color="info"
                  >
                    {subject.class_code ? 'コード再生成' : 'コード生成'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={subject.code_active ? <ToggleOffIcon /> : <ToggleOnIcon />}
                    onClick={() => handleToggleCodeActive(subject.id, subject.code_active)}
                    disabled={!subject.class_code || loading}
                    color={subject.code_active ? "warning" : "secondary"}
                  >
                    {subject.code_active ? '受付停止' : '受付開始'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}